import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

interface BroadcastListener<T> {
  channel: BroadcastChannel;
  subject: Subject<MessageEvent<T>>;
  onMessage: (event: MessageEvent<T>) => void;
  onMessageError: (event: unknown) => void;
}

@Injectable({ providedIn: 'root' })
export class BroadcastService<T> {
  private listeners: Record<string, BroadcastListener<T>> = {};
  private buildBroadcastListener(channelName: string): BroadcastListener<T> {
    const subject = new Subject<MessageEvent<T>>();
    const onMessage = (event: MessageEvent<T>) => subject.next(event);
    const onMessageError = (error: unknown) =>
      console.log('Error occurred during broadcast message: ', error);
    const channel = new BroadcastChannel(channelName);
    channel.addEventListener('message', onMessage);
    channel.addEventListener('messageerror', onMessageError);
    return { channel, subject, onMessage, onMessageError };
  }
  public close(channelName: string) {
    if (!this.listeners[channelName]) return;
    const { channel, subject, onMessage, onMessageError } = this.listeners[channelName];
    delete this.listeners[channelName];
    channel.removeEventListener('message', onMessage);
    channel.removeEventListener('messageerror', onMessageError);
    channel.close();
    subject.complete();
  }
  public open(channelName: string) {
    if (!this.listeners[channelName]) {
      this.listeners[channelName] = this.buildBroadcastListener(channelName);
    }
  }
  public listen$(channelName: string): Observable<T> {
    this.open(channelName);
    const listener = this.listeners[channelName];
    return new Observable((observer) => {
      const sub = listener.subject.pipe(map((event) => event.data)).subscribe(observer);
      return () => {
        sub.unsubscribe();
        if (!listener.subject.observed) this.close(channelName);
      };
    });
  }
  public listen(channelName: string, cb: (data: T) => void): () => void {
    const sub = this.listen$(channelName).subscribe((event) => cb(event));
    return () => sub.unsubscribe();
  }
  /**
   * Broadcasts event to channel
   *
   * @param channelName - channel to broadcast to
   * @param event - data to broadcast
   */
  public broadcast(channelName: string, event: T) {
    this.open(channelName);
    this.listeners[channelName]?.channel.postMessage(event);
  }
}
